import type { InteractiveAnswer, PendingBlock } from '@/_shared/types';

import { ApprovalCard } from './ApprovalCard';
import { MultiSelectQuestion } from './MultiSelectQuestion';
import { QuestionWizard } from './QuestionWizard';
import { SingleSelectQuestion } from './SingleSelectQuestion';

interface Props {
  block: PendingBlock;
  onResolve: (answer: InteractiveAnswer) => void;
  /** True while an answer is being sent (answerInteractive in flight) — locks the controls. */
  submitting?: boolean;
}

/**
 * Dispatches a pending interactive block to its renderer. This is the composer-takeover entry point:
 * ChatConversation renders it in place of the MessageInput while a block is awaiting input.
 *
 * 1 question → single- or multi-select; 2–5 questions → wizard; approval → approval card.
 */
export function PendingQuestion({ block, onResolve, submitting }: Props): React.ReactElement {
  if (block.kind === 'approval') {
    return <ApprovalCard payload={block.payload} onResolve={onResolve} submitting={submitting} />;
  }
  if (block.payload.questions.length > 1) {
    return <QuestionWizard payload={block.payload} onResolve={onResolve} submitting={submitting} />;
  }
  if (block.payload.questions[0]?.multiSelect) {
    return <MultiSelectQuestion payload={block.payload} onResolve={onResolve} submitting={submitting} />;
  }
  return <SingleSelectQuestion payload={block.payload} onResolve={onResolve} submitting={submitting} />;
}
